import { useNavigate } from '@tanstack/react-router';
import { create } from 'zustand';
import { resolveTab, type TabIcon } from './nav';

/**
 * Workspace tabs ("browser-like" strip of open pages, 01-design-system §6.3 / 02-constructor §1.8).
 * One list per user, persisted in localStorage under `atnt.tabs.<userId>`.
 *
 * A tab is identified by `key`. Pages of the same module share one key (all device-registry pages live in the
 * "Qurilma konfiguratsiya" tab) — see `resolveTab()` in nav.ts. Pages may also open tabs by path only; the key is
 * then derived from the path, so `open()` from a page and the shell's own route sync never create duplicates.
 */
export interface ShellTab {
  key: string;
  /** where the tab returns to: pathname + search (+ hash) */
  path: string;
  /** literal title (pages, `rename`) — wins over `titleKey` */
  title?: string;
  /** `shell` i18n key for module tabs, so the title follows the UI language */
  titleKey?: string;
  icon?: TabIcon;
  tenantSlug?: string;
  /** unsaved changes: shows a dot and asks before closing */
  dirty?: boolean;
  /** pinned (pushpin, 317:7084): kept at the start of the strip and never dropped by the MAX_TABS cap */
  pinned?: boolean;
  usedAt: number;
}

export interface OpenTabInput {
  path: string;
  key?: string;
  title?: string;
  titleKey?: string;
  icon?: TabIcon;
  tenantSlug?: string;
}

interface TabsState {
  userId: string | null;
  tabs: ShellTab[];
  activeKey: string | null;
}

/** Hard cap; the least recently used clean tab is dropped beyond it. */
export const MAX_TABS = 12;

const storageKey = (userId: string) => `atnt.tabs.${userId}`;

function load(userId: string): ShellTab[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const data: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(data)) return [];
    return data
      .filter((t): t is ShellTab => Boolean(t) && typeof t.key === 'string' && typeof t.path === 'string' && t.path.startsWith('/'))
      .map((t) => ({ ...t, dirty: false, usedAt: Number(t.usedAt) || 0 }))
      .slice(0, MAX_TABS);
  } catch {
    return [];
  }
}

export const useTabsStore = create<TabsState>()(() => ({ userId: null, tabs: [], activeKey: null }));

useTabsStore.subscribe((state, prev) => {
  if (!state.userId || (state.tabs === prev.tabs && state.userId === prev.userId)) return;
  try {
    localStorage.setItem(storageKey(state.userId), JSON.stringify(state.tabs.map(({ dirty: _dirty, ...t }) => t)));
  } catch {
    /* storage full or blocked — tabs simply won't persist */
  }
});

/** Switch the store to the signed-in user's tabs (idempotent). */
export function bindTabsUser(userId: string) {
  if (useTabsStore.getState().userId === userId) return;
  useTabsStore.setState({ userId, tabs: load(userId), activeKey: null });
}

const pathOnly = (path: string) => path.split(/[?#]/, 1)[0]!;

/** Index of the tab addressed by key, by path, or by the module key the path resolves to. */
function indexOf(tabs: ShellTab[], keyOrPath: string): number {
  let i = tabs.findIndex((t) => t.key === keyOrPath);
  if (i < 0) i = tabs.findIndex((t) => t.path === keyOrPath);
  if (i < 0 && keyOrPath.startsWith('/')) {
    const key = resolveTab(pathOnly(keyOrPath))?.key;
    if (key) i = tabs.findIndex((t) => t.key === key);
  }
  return i;
}

function upsert(input: OpenTabInput, mode: 'open' | 'sync') {
  const { tabs, activeKey } = useTabsStore.getState();
  const key = input.key ?? resolveTab(pathOnly(input.path))?.key ?? input.path;
  const now = Date.now();
  let i = tabs.findIndex((t) => t.key === key);
  if (i < 0) i = tabs.findIndex((t) => t.path === input.path);

  if (i >= 0) {
    const prev = tabs[i]!;
    const next: ShellTab =
      mode === 'sync'
        ? // the route sync only follows navigation; titles/icons set by pages stay
          { ...prev, key, path: input.path, usedAt: now, icon: prev.icon ?? input.icon, tenantSlug: prev.tenantSlug ?? input.tenantSlug }
        : {
            ...prev,
            key,
            path: input.path,
            usedAt: now,
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.titleKey !== undefined ? { titleKey: input.titleKey, title: input.title } : {}),
            ...(input.icon ? { icon: input.icon } : {}),
            ...(input.tenantSlug ? { tenantSlug: input.tenantSlug } : {}),
          };
    if (mode === 'sync' && prev.key === key && prev.path === next.path && activeKey === key) return;
    useTabsStore.setState({ tabs: tabs.map((t, j) => (j === i ? next : t)), activeKey: key });
    return;
  }

  const tab: ShellTab = {
    key,
    path: input.path,
    title: input.title,
    titleKey: input.titleKey,
    icon: input.icon,
    tenantSlug: input.tenantSlug,
    usedAt: now,
  };
  // new tabs open to the right of the active one (02-constructor §1.8)
  const activeIndex = tabs.findIndex((t) => t.key === activeKey);
  const at = activeIndex >= 0 ? activeIndex + 1 : tabs.length;
  let list = [...tabs.slice(0, at), tab, ...tabs.slice(at)];
  while (list.length > MAX_TABS) {
    const victim = list.filter((t) => t.key !== key && !t.dirty && !t.pinned).toSorted((a, b) => a.usedAt - b.usedAt)[0];
    if (!victim) break;
    list = list.filter((t) => t !== victim);
  }
  useTabsStore.setState({ tabs: list, activeKey: key });
}

/** Called by the shell on every navigation: focus (or create) the tab of the current page. */
export function syncRouteTab(href: string, pathname: string) {
  const spec = resolveTab(pathname);
  if (!spec) {
    if (useTabsStore.getState().activeKey !== null) useTabsStore.setState({ activeKey: null });
    return;
  }
  const { tabs } = useTabsStore.getState();
  // first visit: start with the tenant's "Bosh panel", like every frame of the design
  if (!tabs.length && spec.tenantSlug && !spec.key.endsWith(':home')) {
    const home = resolveTab(`/t/${spec.tenantSlug}`)!;
    useTabsStore.setState({
      tabs: [{ key: home.key, path: `/t/${spec.tenantSlug}`, titleKey: home.titleKey, icon: home.icon, tenantSlug: spec.tenantSlug, usedAt: 0 }],
      activeKey: home.key,
    });
  }
  upsert({ path: href, key: spec.key, titleKey: spec.titleKey, icon: spec.icon, tenantSlug: spec.tenantSlug }, 'sync');
}

function closeTab(keyOrPath: string): { closed: ShellTab; next: ShellTab | null; wasActive: boolean } | null {
  const { tabs, activeKey } = useTabsStore.getState();
  const i = indexOf(tabs, keyOrPath);
  if (i < 0) return null;
  const closed = tabs[i]!;
  const rest = tabs.filter((_, j) => j !== i);
  const wasActive = closed.key === activeKey;
  // activate the right neighbour, else the left one
  const next = wasActive ? (rest[i] ?? rest[i - 1] ?? null) : null;
  useTabsStore.setState({ tabs: rest, activeKey: wasActive ? (next?.key ?? null) : activeKey });
  return { closed, next, wasActive };
}

function patch(keyOrPath: string, update: (t: ShellTab) => ShellTab) {
  const { tabs } = useTabsStore.getState();
  const i = indexOf(tabs, keyOrPath);
  if (i < 0) return;
  useTabsStore.setState({ tabs: tabs.map((t, j) => (j === i ? update(t) : t)) });
}

/** Store actions that don't navigate — usable outside React too. */
export const tabActions = {
  /** Create or update a tab and make it active. Matching is by `key`, else by `path`. */
  open: (input: OpenTabInput) => upsert(input, 'open'),
  rename: (keyOrPath: string, title: string) => patch(keyOrPath, (t) => ({ ...t, title, titleKey: undefined })),
  setIcon: (keyOrPath: string, icon: TabIcon) => patch(keyOrPath, (t) => ({ ...t, icon })),
  setDirty: (keyOrPath: string, dirty: boolean) => patch(keyOrPath, (t) => (Boolean(t.dirty) === dirty ? t : { ...t, dirty })),
  /** Pin / unpin: pinned tabs move to the end of the pinned group at the start of the strip. */
  togglePin(key: string) {
    const { tabs } = useTabsStore.getState();
    const tab = tabs.find((t) => t.key === key);
    if (!tab) return;
    const next = { ...tab, pinned: !tab.pinned };
    const rest = tabs.filter((t) => t.key !== key);
    const at = rest.filter((t) => t.pinned).length;
    useTabsStore.setState({ tabs: [...rest.slice(0, at), next, ...rest.slice(at)] });
  },
  /** Reorder (drag & drop). */
  move(fromKey: string, toIndex: number) {
    const { tabs } = useTabsStore.getState();
    const from = tabs.findIndex((t) => t.key === fromKey);
    if (from < 0) return;
    const list = tabs.filter((_, j) => j !== from);
    list.splice(Math.max(0, Math.min(toIndex, list.length)), 0, tabs[from]!);
    useTabsStore.setState({ tabs: list });
  },
  get: (keyOrPath: string): ShellTab | undefined => {
    const { tabs } = useTabsStore.getState();
    return tabs[indexOf(tabs, keyOrPath)];
  },
};

/** Where to go when the last tab closes. */
const homeOf = (tab: ShellTab) => (tab.tenantSlug ? `/t/${tab.tenantSlug}` : '/');

/**
 * Tabs API for pages and features:
 *
 *   const tabs = useTabs();
 *   tabs.open({ path: location.href, title: section.name, icon: { name: 'drop-line', color: section.color } });
 *   tabs.rename(path, 'Yangi nom');   tabs.setDirty(path, true);   tabs.close(path);
 *
 * `close` / `activate` navigate when the active tab changes.
 */
export function useTabs() {
  const navigate = useNavigate();
  const go = (href: string) => void navigate({ href });
  return {
    ...tabActions,
    close(keyOrPath: string) {
      const r = closeTab(keyOrPath);
      if (r?.wasActive) go(r.next?.path ?? homeOf(r.closed));
    },
    activate(keyOrPath: string) {
      const tab = tabActions.get(keyOrPath);
      if (tab) go(tab.path);
    },
  };
}
